// Full Updated File: pages/Register.jsx
// Professional redesign inspired by checkout and order pages
// Premium design with improved structure, spacing, and visual hierarchy

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { User, Mail, Phone, MapPin, Lock, UserPlus, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import './css/RegisterStyles.css';

const Register = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      await authAPI.register({ firstName, lastName, email, phoneNumber, shippingAddress, password });
      // Show success and redirect
      navigate('/login', { state: { message: 'Inscription reussie ! Veuillez vous connecter.' } });
    } catch (err) {
      setError(err.response?.data?.message || "Echec de l'inscription. Veuillez reessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reg-container">
      {/* Header */}
      <div className="reg-header-bar">
        <button onClick={() => navigate('/')} className="reg-back-button">
          <ArrowLeft size={20} />
          <span>Retour a l'accueil</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="reg-content">
        <div className="reg-hero-section">
          <div className="reg-hero-icon-wrapper">
            <UserPlus size={48} className="reg-hero-icon" />
          </div>
          <h1 className="reg-hero-title">Creez votre compte</h1>
          <p className="reg-hero-subtitle">
            Rejoignez-nous et commencez vos achats. Cela ne prend qu'une minute !
          </p>
        </div>

        <div className="reg-form-wrapper">
          <div className="reg-card">
            {error && (
              <div className="reg-error-banner" role="alert">
                <AlertCircle size={18} className="reg-error-icon" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="reg-form">
              {/* Personal Information Section */}
              <div className="reg-section">
                <h2 className="reg-section-title">
                  <User size={18} />
                  Informations personnelles
                </h2>

                <div className="reg-form-row">
                  <div className="reg-form-group">
                    <label className="reg-label">Prenom</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="reg-input"
                      placeholder="John"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="reg-form-group">
                    <label className="reg-label">Nom</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="reg-input"
                      placeholder="Doe"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="reg-section">
                <h2 className="reg-section-title">
                  <Mail size={18} />
                  Informations de contact
                </h2>

                <div className="reg-form-group">
                  <label className="reg-label">Adresse email</label>
                  <div className="reg-input-wrapper">
                    <Mail size={18} className="reg-input-icon" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="reg-input reg-input-with-icon"
                      placeholder="john.doe@example.com"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="reg-form-group">
                  <label className="reg-label">Numero de telephone</label>
                  <div className="reg-input-wrapper">
                    <Phone size={18} className="reg-input-icon" />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      className="reg-input reg-input-with-icon"
                      placeholder="+1 (555) 000-0000"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Information Section */}
              <div className="reg-section">
                <h2 className="reg-section-title">
                  <MapPin size={18} />
                  Informations de livraison
                </h2>

                <div className="reg-form-group">
                  <label className="reg-label">Adresse de livraison</label>
                  <textarea
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    required
                    className="reg-textarea"
                    placeholder="Adresse, ville, code postal"
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <small className="reg-hint">
                    Entrez votre adresse complete : rue, ville et code postal
                  </small>
                </div>
              </div>

              {/* Security Section */}
              <div className="reg-section">
                <h2 className="reg-section-title">
                  <Lock size={18} />
                  Securite
                </h2>

                <div className="reg-form-group">
                  <label className="reg-label">Mot de passe</label>
                  <div className="reg-input-wrapper">
                    <Lock size={18} className="reg-input-icon" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="reg-input reg-input-with-icon"
                    placeholder="Creez un mot de passe solide"
                      disabled={isSubmitting}
                    />
                  </div>
                  <small className="reg-hint">
                    Le mot de passe doit contenir au moins 8 caracteres
                  </small>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="reg-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="reg-spinner"></div>
                    <span>Creation du compte...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    <span>Creer un compte</span>
                  </>
                )}
              </button>

              {/* Login Link */}
              <div className="reg-footer">
                <p className="reg-footer-text">
                  Vous avez deja un compte ?{' '}
                  <Link to="/login" className="reg-footer-link">
                    Connectez-vous
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Info Box */}
          <div className="reg-info-box">
            <CheckCircle size={20} className="reg-info-icon" />
            <div className="reg-info-content">
              <h3 className="reg-info-title">Pourquoi creer un compte ?</h3>
              <ul className="reg-info-list">
                <li>Suivez vos commandes en temps reel</li>
                <li>Enregistrez vos infos de livraison</li>
                <li>Paiement plus rapide</li>
                <li>Acces aux offres et promotions exclusives</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
